import { Link, useLocalSearchParams } from "expo-router"
import { View, ActivityIndicator, Pressable } from "react-native"
import Icon from "@react-native-vector-icons/material-design-icons"
import { SafeAreaView } from "react-native-safe-area-context"

import { ar } from "@src/i18n/ar"
import { Header } from "@src/components/Header"
import { Text } from "@src/components/core"
import { trpc, formatDateForDisplay, sharedStyles, priceFromDBFormat } from "@src/utils"
import { List } from "@src/components/list"

const data = {
  wallet: { title: ar.wallet, icon: <Icon name="wallet" color="#4B5154" size={28} /> },
  visa: { title: ar.visa, icon: <Icon name="credit-card" color="#1E88E5" size={28} /> },
  cash: { title: ar.cash, icon: <Icon name="cash" color="#FFC107" size={28} /> },
  total: { title: ar.total, icon: null },
}

export default function Balance() {
  const params = useLocalSearchParams<{ type: keyof typeof data }>()
  const balance = trpc.balance.get.useQuery()
  const track = trpc.balance.track.useInfiniteQuery(
    { type: params.type },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.nextCursor
      },
    },
  )

  const wallet = balance.data?.wallet ?? 0
  const visa = balance.data?.visa ?? 0
  const cash = balance.data?.cash ?? 0
  const total = wallet + visa + cash

  const balanceValue = { wallet, visa, cash, total }[params.type]

  return (
    <>
      <Header
        title={data[params.type].title}
        right={
          <View className="px-2 h-full justify-center">
            <Text className="font-bold text-lg">
              {priceFromDBFormat(balanceValue).toFixed(2)} {ar.egp}
            </Text>
          </View>
        }
      />
      <List
        contentContainerStyle={sharedStyles.contentContainer}
        data={track.data?.pages.flatMap((page) => page.tracks) ?? []}
        onRefresh={track.refetch}
        onEndReached={() => {
          if (track.hasNextPage && !track.isFetchingNextPage) track.fetchNextPage()
        }}
        onEndReachedThreshold={0.3}
        keyExtractor={(item) => item.id.toString()}
        ListEmptyComponent={() => (
          <View className="flex-1 justify-center items-center p-8">
            {track.isLoading ? <ActivityIndicator /> : <Text className="text-lg text-gray-500">{ar.noTransactions}</Text>}
          </View>
        )}
        renderItem={({ item }) => (
          <View className="bg-white shadow-sm border border-gray-100 rounded mx-4 mb-2 p-4">
            <View className="flex-row justify-between items-center mb-2">
              <Text className="text-left text-lg font-bold text-gray-800">{ar[item.type] ?? item.type}</Text>
              <Text className={`text-lg text-left font-bold ${item.amount > 0 ? "text-green-500" : "text-red-500"}`}>
                {item.amount > 0 ? "+" : ""}
                {priceFromDBFormat(item.amount).toFixed(2)} {ar.egp}
              </Text>
            </View>
            {item.employee && (
              <Link href={`/dashboard/employee/${item.employee.id}`} asChild>
                <Pressable>
                  <Text className="text-sm text-gray-600 mb-4 text-left">{item.employee.name}</Text>
                </Pressable>
              </Link>
            )}
            {item.description && <Text className="text-sm text-gray-600 mb-4 text-left">{item.description}</Text>}
            <View className="flex-row justify-between border-t border-gray-200 pt-2">
              <View className="items-center">
                <Text className="text-xs text-gray-500">{ar.balanceBefore}</Text>
                <Text className="text-md font-semibold text-left text-gray-700">{priceFromDBFormat(item.previousBalance).toFixed(2)}</Text>
              </View>
              <View className="items-center">
                <Text className="text-xs text-gray-500">{ar.balanceAfter}</Text>
                <Text className="text-md font-semibold text-left text-gray-700">{priceFromDBFormat(item.newBalance).toFixed(2)}</Text>
              </View>
            </View>
            <View className="flex-row justify-between py-2">
              <Text className="text-xs text-gray-400 text-left mt-4">{formatDateForDisplay(item.createdAt)}</Text>
              {data[item.balanceType]?.icon}
            </View>
          </View>
        )}
        ListFooterComponent={
          <>
            {track.isFetchingNextPage && (
              <View className="py-4 items-center">
                <ActivityIndicator size="small" />
              </View>
            )}
            <SafeAreaView />
          </>
        }
      />
    </>
  )
}
